/**
 * Main-thread scheduling primitives for interaction responsiveness (INP).
 *
 * Marketing-page INP is dominated by shared third-party work that used to run
 * inside the task of the interaction that triggered it — most visibly the
 * cookie banner's "Accept all", which initialised every analytics provider
 * before the next paint (draft 8, §2.2 A1–A3). These helpers split that work
 * into later tasks so the browser can paint the response first.
 *
 * PRIVACY RULE FOR CALLERS. Deferring work only ever moves a GRANT later.
 * Every deferred callback that starts, loads or re-enables an optional
 * provider must re-read the authoritative consent cookie (and, where the
 * provider is URL-sensitive, the live URL) when it RUNS, never trust the state
 * that existed when it was scheduled. Withdrawals stay synchronous at their
 * call sites and are never routed through these helpers.
 */

type CancelTask = () => void;

interface YieldingScheduler {
  yield?: () => Promise<void>;
}

function timeoutYield(): Promise<void> {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

/**
 * Resolve in a later task so the browser can render the current state first.
 * `scheduler.yield()` keeps the continuation ahead of other queued tasks in
 * Chromium; elsewhere a zero-delay timeout is the portable equivalent.
 */
export function yieldToMain(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  const scheduler = (globalThis as { scheduler?: YieldingScheduler }).scheduler;
  if (scheduler && typeof scheduler.yield === "function") {
    try {
      return scheduler.yield().catch(() => timeoutYield());
    } catch {
      // Fall through to the portable path.
    }
  }
  return timeoutYield();
}

/** Run `callback` in its own macrotask. Returns a cancel function. */
export function scheduleTask(callback: () => void): CancelTask {
  if (typeof window === "undefined") return () => {};
  let cancelled = false;
  const handle = window.setTimeout(() => {
    if (!cancelled) callback();
  }, 0);
  return () => {
    cancelled = true;
    window.clearTimeout(handle);
  };
}

export interface IdleTaskOptions {
  /** Upper bound before the callback runs even if the thread never idles. */
  timeout?: number;
  /** Delay used where `requestIdleCallback` is unavailable (Safari). */
  fallbackDelayMs?: number;
}

/**
 * Run `callback` when the main thread is idle (bounded by `timeout`), in its
 * own task. Browsers without `requestIdleCallback` (Safari) fall back to a
 * timeout (`fallbackDelayMs`, default 0): still off the current task, never on
 * the input path.
 */
export function scheduleIdleTask(
  callback: () => void,
  { timeout = 2_000, fallbackDelayMs = 0 }: IdleTaskOptions = {},
): CancelTask {
  if (typeof window === "undefined") return () => {};
  let cancelled = false;
  const run = () => {
    if (!cancelled) callback();
  };
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(run, { timeout });
    return () => {
      cancelled = true;
      window.cancelIdleCallback?.(handle);
    };
  }
  const handle = window.setTimeout(run, fallbackDelayMs);
  return () => {
    cancelled = true;
    window.clearTimeout(handle);
  };
}
